import createAuth0Client from '@auth0/auth0-spa-js';
import { captureException } from '@sentry/react';
import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getUrlParam, isElectron, isExternalSession, noop, setLocalStorage } from '../../utils';

const isAppRedirectPage = () => window.location.pathname.includes('app-launch');
const isSSOLoginPage = () => window.location.pathname.includes('login');

const config = {
  domain: process.env.REACT_APP_AUTH_DOMAIN,
  client_id: process.env.REACT_APP_AUTH_CLIENT_ID,
  audience: process.env.REACT_APP_AUTH_AUDIENCE,
  redirect_uri: window.location.origin,
  useCookiesForTransactions: true
};

export const createChatgenieAuth0 = () => createAuth0Client(config);

export const logout = async (...p) => {
  if (isElectron()) {
    const remote = window.require('@electron/remote');
    const authProcess = remote.require('./main');
    authProcess.createLogoutWindow();
  } else {
    const client = await createChatgenieAuth0();
    client.logout({
      returnTo: window.location.origin,
      ...p
    });
  }
};

export const Auth0Context = createContext();

export const useAuth0 = () => useContext(Auth0Context);

export const useLoginWithRedirect = ({
  path = window.location.pathname,
  shouldFetchToken = false,
  onSuccess = noop,
  onError = noop
}) => {
  const { loading, getTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();

  useEffect(() => {
    if (isElectron() || isAppRedirectPage() || loading || isAuthenticated || isSSOLoginPage()) {
      return;
    }
    loginWithRedirect({ appState: { path } }).catch(err => {
      captureException(err, {
        tags: {
          service: 'auth0'
        }
      });
    });
  }, [loading, isAuthenticated, loginWithRedirect, path]);

  useEffect(() => {
    if (!loading && !isAppRedirectPage() && isAuthenticated && shouldFetchToken) {
      getTokenSilently()
        .then(token => onSuccess(token))
        .catch(err => {
          onError(err);
          captureException(err, {
            tags: {
              service: 'auth0'
            }
          });
        });
    }
  }, [loading, isAuthenticated]);

  return { loading, isAuthenticated };
};

export const Auth0Provider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const history = useHistory();

  const initAuth0 = async () => {
    try {
      const auth0FromHook = await createChatgenieAuth0();
      setAuth0(auth0FromHook);

      if (isAppRedirectPage()) {
        setLoading(false);
        return;
      }

      if (window.location.search.includes('chatType')) {
        setLocalStorage({
          code: getUrlParam('code'),
          state: getUrlParam('state') && JSON.parse(decodeURIComponent(getUrlParam('state')))
        });

        history.replace('/signup/chat');
      }

      if (window.location.search.includes('code=')) {
        const { appState } = await auth0FromHook.handleRedirectCallback();
        history.replace(appState?.path || '/');
      }

      let authenticated = await auth0FromHook.isAuthenticated();

      if (isElectron()) {
        const remote = window.require('@electron/remote');
        const authService = remote.require('./services/auth-service');
        authenticated = !!authService.getAccessToken();
      }

      setIsAuthenticated(authenticated);

      if (authenticated) {
        let profile = await auth0FromHook.getUser();
        if (isElectron()) {
          const remote = window.require('@electron/remote');
          const authService = remote.require('./services/auth-service');
          profile = authService.getProfile();
        }
        setUser(profile);
      }
    } catch (err) {
      captureException(`Authentication failed: ${err?.message}`, {
        tags: {
          service: 'auth0'
        }
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!isExternalSession()) {
      initAuth0();
    }
  }, []);

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);
    try {
      await auth0Client.loginWithPopup(params);
    } catch (error) {
      // eslint-disable-next-line no-new
      new Error(error);
    } finally {
      setPopupOpen(false);
    }
    const profile = await auth0Client.getUser();
    setUser(profile);
    setIsAuthenticated(true);
  };

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client.handleRedirectCallback();
    const profile = await auth0Client.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(profile);
  };
  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        setAuth0Loading: setLoading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims: (...p) => auth0Client.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => auth0Client.loginWithRedirect(...p),
        getTokenSilently: (...p) => {
          if (isElectron()) {
            const remote = window.require('@electron/remote');
            const authService = remote.require('./services/auth-service');
            return new Promise(resolve => resolve(authService.getAccessToken()));
          }
          return auth0Client.getTokenSilently(...p);
        },
        getTokenWithPopup: (...p) => auth0Client.getTokenWithPopup(...p),
        logout,
        buildAuthorizeUrl: (...p) => auth0Client.buildAuthorizeUrl(...p)
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};

Auth0Provider.propTypes = {
  children: PropTypes.node
};

Auth0Provider.defaultProps = {
  children: ''
};
